import type { VaultElement } from "@p0security/integrations-vaulted-credential-shared/components";
import type { ElementConfigOf } from "@p0security/shared/install/types";

// The install offers no Azure Key Vault (see `withoutAzureKeyVault`).
type VaultType = Exclude<ElementConfigOf<VaultElement>["type"], "azure-kv">;

// AWS's rule for a secret's name. It admits no whitespace, colon or wildcard,
// so neither a pasted key, an ARN nor a pattern matches.
const AWS_SECRET_NAME = /^[A-Za-z0-9/_+=.@-]{1,512}$/;

// Google's rule for a secret's name, its ID. It admits no whitespace, slash or
// wildcard, so neither a pasted key, a resource name nor a pattern matches.
// Every name it admits, AWS's admits too.
const GCP_SECRET_NAME = /^[A-Za-z0-9_-]{1,255}$/;

// The hyphen and six letters or digits that Secrets Manager adds to a secret's
// name in its full ARN. It reads a partial ARN that ends this way as a full
// one, and finds no secret by it.
const AWS_ARN_SUFFIX = /-[A-Za-z0-9]{6}$/;

/** Refuses anything that isn't an AWS secret's name, such as its ARN or a
 * pasted key, without repeating it. */
export const INVALID_SECRET_NAME =
  "That doesn't look like a secret name. Enter the name of the secret that holds the key, not its ARN or the key itself.";

/** Refuses anything that isn't a Google secret's name, such as its resource
 * name or a pasted key, without repeating it. */
export const INVALID_GCP_SECRET_NAME =
  "That doesn't look like a secret name. Enter the name of the secret that holds the key, not its resource name or the key itself.";

/** Refuses a name that Secrets Manager would read as ending in a full ARN's
 * suffix. */
export const SUFFIXED_SECRET_NAME =
  "Secrets Manager can't find a secret by its name when the name ends in a hyphen and six characters, like -AbCdEf. Use a secret whose name doesn't end that way.";

/** A rule for a secret's name, and what to enter when it's broken. */
type SecretNameRule = { message: string; test: (name: string) => boolean };

const SECRET_NAME_RULES: {
  readonly [V in VaultType]: readonly SecretNameRule[];
} = {
  "aws-sm": [
    {
      message: INVALID_SECRET_NAME,
      test: (name) => AWS_SECRET_NAME.test(name),
    },
    {
      message: SUFFIXED_SECRET_NAME,
      test: (name) => !AWS_ARN_SUFFIX.test(name),
    },
  ],
  "gcp-sm": [
    {
      message: INVALID_GCP_SECRET_NAME,
      test: (name) => GCP_SECRET_NAME.test(name),
    },
  ],
};

/**
 * Why `name`, ignoring surrounding whitespace, can't name the private key's
 * secret in a vault of type `vaultType`, or `undefined` if it can.
 *
 * P0 points the connector at the secret by the name in the vault's account
 * and region (see {@link toSecretPartialArn}), or in its project (see
 * {@link toSecretResourceName}), so the name must be one that the vault finds
 * by it. Without `vaultType`, as when the install form's field is checked on
 * its own, `name` is checked as a name that either vault could take, and
 * installing checks it against the item's vault. Each problem is a fixed
 * message: none repeats `name`, which may be a key pasted in its place.
 */
export const findSecretNameProblem = (
  name: unknown,
  vaultType?: VaultType
): string | undefined => {
  const candidate = typeof name === "string" ? name.trim() : undefined;
  const findProblem = (rules: readonly SecretNameRule[]) =>
    candidate === undefined
      ? rules[0]?.message
      : rules.find(({ test }) => !test(candidate))?.message;
  if (vaultType) return findProblem(SECRET_NAME_RULES[vaultType]);
  // A name either vault takes is fine. One that neither does is refused for
  // AWS's reason, the vault element's default.
  const awsProblem = findProblem(SECRET_NAME_RULES["aws-sm"]);
  return awsProblem && findProblem(SECRET_NAME_RULES["gcp-sm"])
    ? awsProblem
    : undefined;
};

/**
 * The partial ARN of the AWS Secrets Manager secret `name`, in `accountId` and
 * `region`: the secret's ARN without the hyphen and six random characters that
 * Secrets Manager adds to its name.
 *
 * P0 points the connector at the private key's secret by it, since Secrets
 * Manager finds a secret by its partial ARN from another account too. The
 * connector's Terraform grants read on it followed by `-??????`.
 *
 * Each value is trimmed, as P0 stores it. P0 takes only regions in AWS's
 * commercial partition, so the partition is `aws`.
 */
export const toSecretPartialArn = ({
  accountId,
  name,
  region,
}: {
  accountId: string;
  name: string;
  region: string;
}) =>
  `arn:aws:secretsmanager:${region.trim()}:${accountId.trim()}:secret:${name.trim()}`;

/**
 * The resource name of the Google Secret Manager secret `name`, in
 * `projectId`: `projects/<project>/secrets/<name>`.
 *
 * P0 points the connector at the private key's secret by it, so the connector
 * reads the secret in the vault's project wherever it runs. The connector's
 * Terraform grants read on that one secret.
 *
 * Each value is trimmed, as P0 stores it.
 */
export const toSecretResourceName = ({
  name,
  projectId,
}: {
  name: string;
  projectId: string;
}) => `projects/${projectId.trim()}/secrets/${name.trim()}`;
